import {
	BatteryWarning,
	Bell,
	Calendar,
	CalendarCheck,
	CalendarX,
	HardDrive,
	Info,
	KeyRound,
	CircleAlert,
	TriangleAlert,
	User,
	UserPlus,
	WifiOff,
	Wrench
} from 'lucide-react';
import { zezamiiNeutral, zezamiiSemantic } from 'theme/brandTokens';

/**
 * Presentation config for the Alert Hub (D5a) — the single place mapping the notification
 * taxonomy (severity, eventType/entityType, category) to icons/colours/labels, so rows,
 * badges, and popovers pattern-match a category at a glance. a11y: severity is conveyed by
 * icon + label, never colour alone.
 */

// Categories that route to the ALERTS surface (mirror of the App Server ALERT_CATEGORIES).
export const ALERT_CATEGORIES = ['operational', 'actionable'];
export const isAlertCategory = (category) => ALERT_CATEGORIES.includes(category);

// severity → colour + label + glyph. Info is muted (grey), not brand-coloured (D0 §5).
const SEVERITY_META = {
	critical: { color: zezamiiSemantic.danger, label: 'Critical', Icon: TriangleAlert },
	warning: { color: zezamiiSemantic.warning, label: 'Warning', Icon: CircleAlert },
	info: { color: zezamiiNeutral.grey500, label: 'Info', Icon: Info }
};
export const severityMeta = (severity) => SEVERITY_META[severity] || SEVERITY_META.info;

// Sort weight — critical floats above info within a time bucket.
export const SEVERITY_WEIGHT = { critical: 0, warning: 1, info: 2 };

// eventType → row glyph (specific), then entityType (generic), then the Bell fallback.
const EVENT_ICON = {
	'device.offline': WifiOff,
	'device.low_battery': BatteryWarning,
	'access.granted': KeyRound,
	'door.remote_unlocked': KeyRound,
	'person.invited': UserPlus,
	'booking.created': CalendarCheck,
	'booking.cancelled': CalendarX,
	'maintenance.created': Wrench
};
const ENTITY_ICON = {
	device: HardDrive,
	person: User,
	booking: Calendar,
	credential: KeyRound,
	maintenance: Wrench
};

/** The lucide icon component for a notification row. */
export const eventIconFor = (row) => (row && (EVENT_ICON[row.eventType] || ENTITY_ICON[row.entityType])) || Bell;

// Preferences matrix (D5b): channel columns in display order (In-app · Push · Email · SMS —
// D0 §6), and a plain-language example under each event name so the matrix is self-explaining.
export const CHANNEL_COLUMNS = [
	{ key: 'inapp', label: 'In-app' },
	{ key: 'push', label: 'Push' },
	{ key: 'email', label: 'Email' },
	{ key: 'sms', label: 'SMS' }
];

// Delivery-level presets (B2) — the primary per-event control; channels are the advanced view.
// Mirrors the App Server constants/notificationDeliveryLevels.js ladder. 'custom' is a state
// (a per-channel selection matching no preset), not a selectable preset.
export const DELIVERY_LEVELS = [
	{ value: 'off', label: 'Off' },
	{ value: 'inbox', label: 'Inbox' },
	{ value: 'notify', label: 'Notify' },
	{ value: 'urgent', label: 'Urgent' }
];
export const DELIVERY_LEVEL_HINT = {
	off: 'No notifications',
	inbox: 'In-app only',
	notify: 'In-app, push & email',
	urgent: 'Everything, incl. SMS',
	custom: 'Custom per-channel'
};

// Per-cell origin (B2 hierarchy) → the ghost label under an inherited/locked switch. `personal`
// has no ghost (it's the user's explicit choice); `default` keeps the legacy "Default" wording.
const ORIGIN_LABEL = {
	'site': 'Site default',
	'org': 'Org default',
	'admin-locked': 'Locked by admin',
	'default': 'Default'
};
export const originLabel = (origin) => ORIGIN_LABEL[origin] || null;

const EVENT_EXAMPLES = {
	'device.offline': 'When a device goes offline',
	'device.low_battery': 'When a device battery runs low',
	'access.granted': 'When you are granted access',
	'door.remote_unlocked': 'When a door is remotely unlocked',
	'person.invited': 'When someone is invited to your organisation',
	'booking.created': 'When a booking is created',
	'booking.cancelled': 'When a booking is cancelled',
	'maintenance.created': 'When a maintenance record is created'
};
export const eventExample = (eventType) => EVENT_EXAMPLES[eventType] || null;
